import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Button, Chip, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, Tabs, Tab,
  InputAdornment, CircularProgress, Avatar, Tooltip, MenuItem,
  Select, FormControl, InputLabel, Alert, TablePagination,
} from '@mui/material';
import {
  Search as SearchIcon, Visibility as VisibilityIcon, Upload as UploadIcon,
  Close as CloseIcon, CheckCircle as CheckIcon, HourglassEmpty as PendingIcon,
  ArrowBack as ArrowBackIcon, PictureAsPdf as PdfIcon, Description as DescriptionIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { adminListarDocumentos, adminUploadDocumento, adminObterPdf } from '../../services/adminApi';

const formatDate = (d: string) =>
  d ? new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const statusColor = (s: string) =>
  s === 'ASSINADO' ? 'success' : s === 'PENDENTE' ? 'warning' : 'default';

// ── PDF Viewer ────────────────────────────────────────────────────────────────
const PdfViewerDialog: React.FC<{ open: boolean; doc: any; onClose: () => void }> = ({ open, doc, onClose }) => {
  const [pdfData, setPdfData] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open && doc) loadPdf(); if (!open) setPdfData(null); }, [open, doc]);

  const loadPdf = async () => {
    setLoading(true);
    try {
      const data = await adminObterPdf(doc.id, doc.servidor?.id || doc.servidor_id);
      setPdfData(data.pdf_base64);
    } catch { toast.error('Erro ao carregar PDF'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>{doc?.nome_arquivo}</Typography>
          <Typography variant="caption" color="text.secondary">{doc?.servidor?.nome_completo}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, minHeight: 600 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <CircularProgress />
          </Box>
        ) : pdfData ? (
          <iframe src={`data:application/pdf;base64,${pdfData}`} width="100%" height="600"
            style={{ border: 'none', display: 'block' }} title="PDF Viewer" />
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <Typography color="text.secondary">PDF não disponível</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Fechar</Button></DialogActions>
    </Dialog>
  );
};

// ── Upload Dialog ─────────────────────────────────────────────────────────────
const UploadDialog: React.FC<{ open: boolean; onClose: () => void; onUploaded: () => void }> = ({ open, onClose, onUploaded }) => {
  const [servidorId, setServidorId] = useState('');
  const [tipo, setTipo] = useState('SOLICITACAO');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const reset = () => { setServidorId(''); setTipo('SOLICITACAO'); setFile(null); };

  const handleUpload = async () => {
    if (!servidorId || !file) return;
    setLoading(true);
    try {
      await adminUploadDocumento(parseInt(servidorId), file, tipo);
      toast.success('Documento enviado!');
      onUploaded(); onClose(); reset();
    } catch { toast.error('Erro ao enviar documento'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>Enviar Documento</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField fullWidth label="ID do Servidor" value={servidorId}
              onChange={(e) => setServidorId(e.target.value)} size="small" type="number"
              helperText="Informe o ID numérico do servidor" />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de Documento</InputLabel>
              <Select value={tipo} label="Tipo de Documento" onChange={(e) => setTipo(e.target.value)}>
                <MenuItem value="SOLICITACAO">Solicitação</MenuItem>
                <MenuItem value="CONTRATO">Contrato</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ border: '2px dashed', borderColor: file ? 'success.main' : 'divider',
              borderRadius: 2, p: 3, textAlign: 'center', cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main' } }} component="label">
              <input accept=".pdf" type="file" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} />
              <PdfIcon sx={{ fontSize: 36, color: file ? 'success.main' : 'text.secondary', mb: 1 }} />
              <Typography variant="body2" color={file ? 'success.main' : 'text.secondary'}>
                {file ? file.name : 'Clique para selecionar o arquivo PDF'}
              </Typography>
            </Box>
            {file && <Alert severity="success" sx={{ mt: 1, borderRadius: 2 }}>
              {file.name} ({(file.size / 1024).toFixed(1)} KB)
            </Alert>}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button variant="contained" onClick={handleUpload} disabled={!servidorId || !file || loading}
          startIcon={loading ? <CircularProgress size={16} /> : <UploadIcon />}>Enviar</Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
const AdminDocumentosPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const [documentos, setDocumentos] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [totais, setTotais] = useState({ todos: 0, assinados: 0, pendentes: 0 });
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filtroStatus, setFiltroStatus] = useState<'todos' | 'assinados' | 'pendentes'>(
    (params.get('filtro') as any) || 'todos'
  );
  const [filtroTipo, setFiltroTipo] = useState<string>(params.get('tipo') || 'todos');

  const [viewerOpen, setViewerOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminListarDocumentos({
        page: page + 1,
        limit: rowsPerPage,
        filtro: filtroStatus === 'todos' ? undefined : filtroStatus,
        search: search || undefined,
      });

      // Suporte a resposta paginada { data, total, totais } ou array legado
      if (Array.isArray(res)) {
        setDocumentos(res);
        setTotal(res.length);
        const assinados = res.filter((d: any) => d.status === 'ASSINADO').length;
        const pendentes = res.filter((d: any) => d.status === 'PENDENTE').length;
        setTotais({ todos: res.length, assinados, pendentes });
      } else {
        setDocumentos(res.data || []);
        setTotal(res.total || 0);
        if (res.totais) setTotais(res.totais);
      }
    } catch { toast.error('Erro ao carregar documentos'); }
    finally { setLoading(false); }
  }, [page, rowsPerPage, filtroStatus, search]);

  useEffect(() => { load(); }, [load]);

  // Filtro de tipo é feito no frontend (não muda paginação)
  const filtered = filtroTipo === 'todos'
    ? documentos
    : documentos.filter((d) => d.tipo === filtroTipo);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/admin')} size="small"><ArrowBackIcon /></IconButton>
          <Box>
            <Typography variant="h5" fontWeight={700}>Documentos</Typography>
            <Typography variant="body2" color="text.secondary">
              Gerenciamento de documentos dos servidores
            </Typography>
          </Box>
          {/* Badges de totais */}
          <Box sx={{ display: 'flex', gap: 1, ml: 1 }}>
            <Chip icon={<DescriptionIcon />}
              label={loading && totais.todos === 0 ? '...' : `${totais.todos} total`}
              color="default" variant="outlined" sx={{ fontWeight: 700 }} />
            <Chip icon={<CheckIcon />}
              label={`${totais.assinados} assinados`}
              color="success" variant="outlined" sx={{ fontWeight: 700 }} />
            <Chip icon={<PendingIcon />}
              label={`${totais.pendentes} pendentes`}
              color="warning" variant="outlined" sx={{ fontWeight: 700 }} />
          </Box>
        </Box>
        <Button variant="contained" startIcon={<UploadIcon />}
          onClick={() => setUploadOpen(true)} sx={{ borderRadius: 2 }}>
          Novo Documento
        </Button>
      </Box>

      <Paper sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        {/* Filters */}
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField placeholder="Pesquisar por servidor, CPF ou arquivo..."
            value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            size="small" sx={{ flex: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary' }} /></InputAdornment>,
              endAdornment: loading && searchInput ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment> : null,
            }} />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Tipo</InputLabel>
            <Select value={filtroTipo} label="Tipo" onChange={(e) => { setFiltroTipo(e.target.value); setPage(0); }}>
              <MenuItem value="todos">Todos os tipos</MenuItem>
              <MenuItem value="CONTRATO">Contrato</MenuItem>
              <MenuItem value="SOLICITACAO">Solicitação</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Tabs com contagens reais do banco */}
        <Tabs value={filtroStatus}
          onChange={(_, v) => { setFiltroStatus(v); setPage(0); }}
          sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}>
          <Tab label={`Todos (${totais.todos})`} value="todos" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label={`Pendentes (${totais.pendentes})`} value="pendentes" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label={`Assinados (${totais.assinados})`} value="assinados" sx={{ textTransform: 'none', fontWeight: 600 }} />
        </Tabs>

        {loading && documentos.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'grey.50' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Servidor</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Documento</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Criado em</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Assinado em</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                  {filtered.map((doc) => (
                    <TableRow key={doc.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light', fontSize: 13 }}>
                            {doc.servidor?.nome_completo?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 160 }}>
                              {doc.servidor?.nome_completo}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{doc.servidor?.cpf}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{doc.nome_arquivo}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={doc.tipo === 'CONTRATO' ? 'Contrato' : 'Solicitação'} size="small"
                          variant="outlined" color={doc.tipo === 'CONTRATO' ? 'secondary' : 'primary'} />
                      </TableCell>
                      <TableCell>
                        <Chip label={doc.status} size="small" color={statusColor(doc.status) as any}
                          icon={doc.status === 'ASSINADO'
                            ? <CheckIcon sx={{ fontSize: '14px !important' }} />
                            : <PendingIcon sx={{ fontSize: '14px !important' }} />} />
                      </TableCell>
                      <TableCell>{formatDate(doc.created_at)}</TableCell>
                      <TableCell>{formatDate(doc.data_assinatura)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Visualizar PDF">
                          <IconButton size="small" color="primary"
                            onClick={() => { setSelectedDoc(doc); setViewerOpen(true); }}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                        Nenhum documento encontrado
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination component="div" count={total} page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
              rowsPerPageOptions={[10, 20, 50, 100]}
              labelRowsPerPage="Por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              sx={{ borderTop: '1px solid', borderColor: 'divider' }} />
          </>
        )}
      </Paper>

      <PdfViewerDialog open={viewerOpen} doc={selectedDoc} onClose={() => setViewerOpen(false)} />
      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={load} />
    </Box>
  );
};

export default AdminDocumentosPage;